import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Sparkles, HelpCircle } from "lucide-react";
import api from "../lib/api";

const HERO_IMAGE = "https://images.unsplash.com/photo-1600891964092-4316c288032e?w=1600&q=85";

const CATEGORY_DESCRIPTIONS = {
  "Techniques": "The verbs of cooking — how to blanch, braise, sear, ferment and more.",
  "Ingredients": "Pantry building blocks — herbs, spices, cheeses, fats and the good stuff behind every dish.",
  "Dishes": "Named foods and preparations — aioli, antipasto, cacio e pepe and their kin.",
  "Tools": "The kit that makes it work — pans, whisks, thermometers and the humble mortar and pestle.",
  "Cuisines": "Regional cooking styles and traditions — Italian, French, Japanese and beyond.",
  "Cooking Terms": "The vocabulary of the kitchen — from al dente to umami, plus the words chefs love to argue about.",
};

export default function Home() {
  const [entries, setEntries] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ entries: 0, recipes: 0, categories: 0 });
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [e, r, c, s] = await Promise.all([
          api.listEntries(),
          api.listRecipes(),
          api.categories(),
          api.stats(),
        ]);
        setEntries(e);
        setRecipes(r);
        setCategories(c.categories || []);
        setStats(s);
      } catch (err) { console.error(err); }
    })();
  }, []);

  const featured = entries.slice(0, 4);
  const featuredRecipes = recipes.slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO — editorial split */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16 grid md:grid-cols-12 gap-8 md:gap-12 items-end">
        <div className="md:col-span-7">
          <div className="font-ui text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] opacity-60 mb-4 md:mb-6">
            Figure it out · Cook it · Eat it or Leave it
          </div>
          <h1 className="font-display text-4xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] sm:leading-[0.9] tracking-tight">
            Every kitchen word,<br />
            <span className="italic" style={{ color: "var(--tomato)" }}>defined deliciously</span>.
          </h1>
          <p className="font-body text-base sm:text-lg md:text-xl leading-relaxed mt-5 md:mt-8 max-w-xl opacity-80">
            A searchable food glossary and recipe companion — hundreds of definitions in a dry, plain-English voice, plus a live dictionary that grows every time you look something up.
          </p>

          <form
            onSubmit={(e) => { e.preventDefault(); window.location.href = `/glossary?q=${encodeURIComponent(q)}`; }}
            className="mt-6 md:mt-10 relative max-w-xl"
            data-testid="hero-search-form"
          >
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 opacity-60" />
            <input
              className="input-editorial"
              placeholder="Search a term or technique…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="hero-search-input"
            />
          </form>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <Link to="/faq" className="btn-primary btn-accent" data-testid="hero-faq-button">
              <HelpCircle size={16} /> Read the FAQ
            </Link>
            <Link to="/glossary" className="btn-primary btn-outline" data-testid="hero-browse-button">
              Browse A–Z
            </Link>
          </div>

          <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-x-6 md:gap-x-8 gap-y-3 font-ui text-xs sm:text-sm uppercase tracking-widest">
            <div><span className="font-display italic text-2xl md:text-3xl mr-2">{stats.entries}</span>Terms</div>
            <div><span className="font-display italic text-2xl md:text-3xl mr-2">{stats.recipes}</span>Recipes</div>
            <div><span className="font-display italic text-2xl md:text-3xl mr-2">{stats.categories}</span>Categories</div>
          </div>
        </div>

        <div className="md:col-span-5 relative" data-testid="hero-image">
          <div className="aspect-[4/5] overflow-hidden border border-black/15 relative">
            <img src={HERO_IMAGE} alt="Seared steak" className="w-full h-full object-cover" />
            <div className="absolute bottom-4 left-4 right-4 font-ui text-xs uppercase tracking-widest text-white/90 flex justify-between">
              <span>Cover</span><span>№ 001</span>
            </div>
          </div>
          <div className="absolute -bottom-4 -right-4 hidden md:block bg-[var(--mustard)] px-4 py-3 font-display italic text-xl border border-black">
            &ldquo;Sear the truth in.&rdquo;
          </div>
        </div>
      </section>

      <div className="editorial-rule max-w-7xl mx-auto" />

      {/* HOW TO USE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-16" data-testid="how-to-use">
        <div className="font-ui text-xs uppercase tracking-[0.3em] opacity-60 mb-3">Start here</div>
        <h2 className="font-display text-4xl md:text-5xl mb-8">How to use this site</h2>

        <div className="grid md:grid-cols-2 gap-10 md:gap-16">
          <div>
            <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-4">Three simple steps</div>
            <ul className="space-y-4 font-body text-lg leading-relaxed">
              <li className="flex gap-4">
                <span className="font-display italic text-3xl leading-none shrink-0 w-10" style={{ color: "var(--tomato)" }}>·</span>
                <span><strong className="font-ui uppercase tracking-wider text-sm mr-2">Search a term</strong>Type any cooking word into the search box — <em>al dente, blanch, umami</em> — and read a plain-English definition. If we haven't defined it yet, we'll write one for you on the spot.</span>
              </li>
              <li className="flex gap-4">
                <span className="font-display italic text-3xl leading-none shrink-0 w-10" style={{ color: "var(--tomato)" }}>·</span>
                <span><strong className="font-ui uppercase tracking-wider text-sm mr-2">Browse by category</strong>Not sure what you're looking for? Tap a category pill — Techniques, Ingredients, Dishes, Cuisines, Tools, Cooking Terms — and wander.</span>
              </li>
              <li className="flex gap-4">
                <span className="font-display italic text-3xl leading-none shrink-0 w-10" style={{ color: "var(--tomato)" }}>·</span>
                <span><strong className="font-ui uppercase tracking-wider text-sm mr-2">Find a recipe</strong>Every term has a recipe that uses or demonstrates it. Open any entry and scroll down to the recipe card, or head straight to Recipes to browse the lot.</span>
              </li>
            </ul>
          </div>

          <div>
            <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-4">The three sections</div>
            <ul className="space-y-4 font-body text-lg leading-relaxed">
              <li className="flex gap-4">
                <span className="font-display italic text-3xl leading-none shrink-0 w-10" style={{ color: "var(--olive)" }}>·</span>
                <span><Link to="/glossary" className="font-ui uppercase tracking-wider text-sm mr-2 hover-underline">Glossary</Link>Every word in the book plus a growing crowd of new ones — search, browse A–Z, or filter by category.</span>
              </li>
              <li className="flex gap-4">
                <span className="font-display italic text-3xl leading-none shrink-0 w-10" style={{ color: "var(--olive)" }}>·</span>
                <span><Link to="/recipes" className="font-ui uppercase tracking-wider text-sm mr-2 hover-underline">Recipes</Link>A dish for every term — with ingredients, timings, method and a witty closing line, in Dr. D's voice.</span>
              </li>
              <li className="flex gap-4">
                <span className="font-display italic text-3xl leading-none shrink-0 w-10" style={{ color: "var(--olive)" }}>·</span>
                <span><Link to="/admin" className="font-ui uppercase tracking-wider text-sm mr-2 hover-underline">Kitchen Desk</Link>The behind-the-scenes editor — add or tidy any term, image or recipe whenever the mood strikes. Password-protected and only used by the author.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <div className="editorial-rule max-w-7xl mx-auto" />

      {/* CONTRIBUTION COUNTER — big growing recipe count */}
      <ContributionCounter total={stats.recipes} />

      <div className="editorial-rule max-w-7xl mx-auto" />

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-ui text-xs uppercase tracking-[0.3em] opacity-60 mb-3">Browse</div>
            <h2 className="font-display text-4xl md:text-5xl">By category</h2>
          </div>
          <Link to="/glossary" className="hidden md:inline-flex items-center gap-2 font-ui text-sm uppercase tracking-widest hover-underline" data-testid="see-all-terms">
            See all terms <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="category-list">
          {categories.map((c) => {
            const desc = CATEGORY_DESCRIPTIONS[c] || "A collection of terms in this category.";
            return (
              <Link
                key={c}
                to={`/glossary?category=${encodeURIComponent(c)}`}
                data-testid={`category-pill-${c.toLowerCase().replace(/\s+/g,'-')}`}
                className="card-editorial p-6 flex flex-col gap-2"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-2xl md:text-3xl">{c}</span>
                  <ArrowRight size={18} className="opacity-60" />
                </div>
                <p className="font-body text-sm md:text-base leading-snug opacity-80">{desc}</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED TERMS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-ui text-xs uppercase tracking-[0.3em] opacity-60 mb-3">In this issue</div>
            <h2 className="font-display text-4xl md:text-5xl italic">Featured terms</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 stagger" data-testid="featured-terms">
          {featured.map((e) => (
            <Link key={e.slug} to={`/term/${e.slug}`} className="card-editorial flex flex-col" data-testid={`featured-term-${e.slug}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={e.image_url} alt={e.term} className="w-full h-full object-cover" />
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="font-ui text-[10px] uppercase tracking-[0.25em] opacity-60 mb-2">{e.category}</div>
                <div className="font-display text-3xl leading-none break-words">{e.term}</div>
                {e.pronunciation && <div className="font-body italic text-sm opacity-70 mt-1">{e.pronunciation}</div>}
                <p className="font-body text-sm mt-3 line-clamp-3 opacity-80">{e.definition}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <div className="editorial-rule max-w-7xl mx-auto" />

      {/* FEATURED RECIPES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-ui text-xs uppercase tracking-[0.3em] opacity-60 mb-3">Cook something</div>
            <h2 className="font-display text-4xl md:text-5xl">Recipes to try</h2>
          </div>
          <Link to="/recipes" className="hidden md:inline-flex items-center gap-2 font-ui text-sm uppercase tracking-widest hover-underline" data-testid="see-all-recipes">
            All recipes <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6 stagger" data-testid="featured-recipes">
          {featuredRecipes.map((r) => (
            <Link key={r.slug} to={`/recipe/${r.slug}`} className="card-editorial flex flex-col" data-testid={`featured-recipe-${r.slug}`}>
              <div className="aspect-[3/4] overflow-hidden">
                <img src={r.image_url} alt={r.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <div className="font-ui text-[10px] uppercase tracking-[0.25em] opacity-60 mb-2">{r.category} · {r.cook_time}</div>
                <div className="font-display text-2xl leading-tight break-words">{r.title}</div>
                <p className="font-body text-sm italic opacity-70 mt-2 break-words">{r.subtitle}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Quote block */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
        <Sparkles className="mx-auto mb-6 opacity-40" size={22} />
        <p className="font-display italic text-3xl md:text-5xl leading-tight">
          &ldquo;A recipe has no soul. You, as the cook, must bring soul to the recipe.&rdquo;
        </p>
        <p className="font-ui text-xs uppercase tracking-[0.3em] opacity-60 mt-6">— Thomas Keller</p>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Contribution Counter                                                       */
/* Shows the live recipe count with a gentle count-up animation on mount so   */
/* returning visitors can see the collection growing.                          */
/* ------------------------------------------------------------------------- */
function ContributionCounter({ total }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!total || total <= 0) { setDisplay(0); return; }
    const duration = 1400; // ms
    const start = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic for a satisfying finish
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(total * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [total]);

  return (
    <section
      className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20 text-center"
      data-testid="contribution-counter"
    >
      <div className="font-ui text-[10px] sm:text-xs uppercase tracking-[0.3em] opacity-60 mb-4">
        The glossary is growing
      </div>
      <div
        className="font-display italic leading-[0.95] text-7xl sm:text-8xl md:text-9xl"
        style={{ color: "var(--olive, #5C6B46)" }}
        data-testid="contribution-counter-number"
      >
        {display.toLocaleString()}
      </div>
      <div className="font-display uppercase tracking-tight text-2xl sm:text-3xl md:text-4xl mt-4">
        Recipes &amp; counting
      </div>
      <p className="font-body italic text-base md:text-lg opacity-70 mt-4 max-w-xl mx-auto">
        Every recipe on Bite &amp; Banter is written or curated by hand. New ones land whenever the mood strikes the kitchen.
      </p>
      <Link
        to="/recipes"
        className="btn-primary btn-outline inline-flex mt-8"
        data-testid="contribution-counter-cta"
      >
        Browse every recipe <ArrowRight size={16} />
      </Link>
    </section>
  );
}
